// A clip's browser-only warp, and the render that carries it to hosts that
// cannot reproduce it (Live: the SDK has no setter for warp markers, pitch or
// the mode knobs; Audiotool: no warp engine). See ClipData.render.
//
// Pure: no Node, no browser. The browser that edits the clip renders it and
// proposes `render`; every other host reads `renderStatus` to decide which
// audio to build from.

import type { AudioClipInfo, Snapshot } from "../seed-history.js";
import type { ClipData, ClipRender } from "./protocol.js";

/** The fields of a clip, and the audio, that shape what its render sounds
 * like. Anything outside this list (name, colour, muted, where it sits) does
 * not change the render. */
type RenderInputs = Pick<
  ClipData,
  | "isWarped"
  | "warpModeValue"
  | "sourceTempo"
  | "startMarker"
  | "endMarker"
  | "loopStart"
  | "loopEnd"
  | "looping"
  | "duration"
  | "transpose"
  | "detune"
  | "warpSettings"
  | "warpMarkers"
> & { audioKey: string | null };

/**
 * A short, stable digest of everything that shapes the clip's sound: equal
 * inputs, equal hash, in every host. FNV-1a over a canonical string (two
 * 32-bit lanes ⇒ 16 hex chars) — an equality check, not security.
 */
export function warpRenderHash(c: RenderInputs): string {
  const num = (n: number | undefined): string => (n === undefined ? "" : String(Math.round(n * 1e6) / 1e6));
  const parts = [
    c.audioKey ?? "",
    c.isWarped ? "1" : "0",
    num(c.warpModeValue),
    num(c.sourceTempo),
    num(c.startMarker),
    num(c.endMarker),
    num(c.loopStart),
    num(c.loopEnd),
    c.looping ? "1" : "0",
    num(c.duration),
    num(c.transpose ?? 0),
    num(c.detune ?? 0),
    JSON.stringify(c.warpSettings ? Object.entries(c.warpSettings).sort(([a], [b]) => (a < b ? -1 : 1)) : []),
    (c.warpMarkers ?? []).map((m) => `${num(m.beat)}:${num(m.sec)}`).join(","),
  ];
  const text = parts.join("|");
  let a = 0x811c9dc5;
  let b = 0x01000193 ^ text.length;
  for (let i = 0; i < text.length; i++) {
    const ch = text.charCodeAt(i);
    a = Math.imul(a ^ ch, 0x01000193) >>> 0;
    b = Math.imul(b ^ ch, 0x85ebca6b) >>> 0;
  }
  return a.toString(16).padStart(8, "0") + b.toString(16).padStart(8, "0");
}

/**
 * Where a clip's render stands:
 *   "none"    the clip carries no render (its warp is Live's own, or flat);
 *   "ready"   the render is of exactly this clip: hosts that need it use it;
 *   "pending" a render is wanted but not (yet) of this clip: no key, or made
 *             from an older version of the clip. The original plays.
 */
export type RenderStatus = "none" | "ready" | "pending";

/**
 * An unwarped clip with no pitch or knobs is its file, played as it is: nothing
 * for a render to carry. It may still hold one from when it was warped, and a
 * host building from that would warp it again (real Live, 2026-10-30).
 */
function playsItsFile(c: Pick<ClipData, "isWarped" | "transpose" | "detune"> & { warpSettings?: object | undefined }): boolean {
  return !c.isWarped && !(c.transpose ?? 0) && !(c.detune ?? 0) && !(c.warpSettings && Object.keys(c.warpSettings).length > 0);
}

export function renderStatus(c: RenderInputs & { render?: ClipRender }): RenderStatus {
  const r = c.render;
  if (!r) return "none";
  if (playsItsFile(c)) return "none";
  return r.key && r.tempo > 0 && r.hash === warpRenderHash(c) ? "ready" : "pending";
}

/**
 * Whether a clip carries warp that only a render can take to a host without
 * a warp engine, whatever it says about wanting one. For clips made before
 * `render` existed, which never asked for it.
 *
 * Certain: pitch (Live has no setter) and the mode knobs. Markers are
 * Live's own on a Session View clip (its source file ships with them), so
 * they say nothing there; on a TIMELINE clip a Set ships a flattened render
 * with none, so markers there, or a source tempo that is not the room's, are
 * the browser's: Live would re-warp the file by its own analysis.
 */
export function needsBrowserRender(
  c: Pick<ClipData, "clipKind" | "isWarped" | "slot" | "transpose" | "detune" | "warpSettings" | "warpMarkers" | "sourceTempo">,
  roomTempo: number,
): boolean {
  if (c.clipKind === "midi") return false;
  if ((c.transpose ?? 0) !== 0 || (c.detune ?? 0) !== 0) return true;
  if (c.warpSettings && Object.keys(c.warpSettings).length > 0) return true;
  if (!c.isWarped || c.slot !== undefined) return false;
  if ((c.warpMarkers?.length ?? 0) >= 2) return true;
  return c.sourceTempo !== undefined && roomTempo > 0 && Math.abs(c.sourceTempo - roomTempo) > 0.01;
}

/**
 * Whether a Live must build a clip from its ready render, because the SDK cannot
 * give it what the clip says: pitch, the mode knobs, a timeline clip's markers.
 * A source tempo that is not the room's alone is not that: Live stretches a
 * warped clip itself, and keeps the musician's warp mode doing it. Unless the
 * clip is NEW here and that tempo is so far off that Live's guess at the file's
 * speed lands on the wrong power of two (clipsync.ts `warpReadyFile`).
 */
export function liveNeedsRender(
  c: Pick<ClipData, "clipKind" | "isWarped" | "slot" | "transpose" | "detune" | "warpSettings" | "warpMarkers" | "sourceTempo">,
  render: Pick<ClipRender, "tempo">,
  isNewHere: boolean,
): boolean {
  if (c.clipKind === "midi") return false;
  if ((c.transpose ?? 0) !== 0 || (c.detune ?? 0) !== 0) return true;
  if (c.warpSettings && Object.keys(c.warpSettings).length > 0) return true;
  if (!c.isWarped) return false; // its file, as it is (`renderStatus`)
  if (c.slot !== undefined) return true;
  if ((c.warpMarkers?.length ?? 0) >= 2) return true;
  if (!isNewHere || c.sourceTempo === undefined || !(c.sourceTempo > 0)) return false;
  const off = render.tempo / c.sourceTempo;
  return off > 1.35 || off < 1 / 1.35;
}

/** The render to build from, or null when the clip has none that is ready. */
export function readyRender(c: RenderInputs & { render?: ClipRender }): (ClipRender & { key: string }) | null {
  return renderStatus(c) === "ready" ? (c.render as ClipRender & { key: string }) : null;
}

/**
 * The clip as a host that builds from the render sees it: one warped object
 * at the render's tempo covering exactly the render, no pitch, no markers,
 * no knobs — the same shape `bounceInPlace` bakes. Region fields are in the
 * render's own beats, which is why they differ from the canonical clip's.
 */
export function renderedClipData<T extends ClipData>(c: T, r: ClipRender): T {
  const baked: T = {
    ...c,
    isWarped: true,
    warpModeValue: 0,
    sourceTempo: r.tempo,
    // A slot clip loops in Live only if it did in the room: a one-shot pad
    // built looping played on where the browser stopped (2026-10-27 audit).
    looping: c.slot !== undefined && c.looping,
    startMarker: 0,
    endMarker: c.duration,
    loopStart: 0,
    loopEnd: c.duration,
    transpose: 0,
    detune: 0,
  };
  delete baked.warpMarkers;
  delete baked.warpSettings;
  delete baked.render;
  return baked;
}

/**
 * A seed's arrangement as a host without a warp engine reads it: every clip
 * carrying a published render is replaced by that render, shaped as a source
 * clip warped at the render's tempo covering exactly the render (what
 * `renderedClipData` is for a room's clip). The browser, which has the
 * engine, reads the arrangement as it is. A clip without a render, and
 * MIDI, pass through untouched.
 */
export function withRenders(arrangement: Snapshot): Snapshot {
  const bake = (c: AudioClipInfo): AudioClipInfo => {
    if (!c.render || playsItsFile({ ...c, isWarped: c.warping })) return c;
    const { render, warpMarkers: _m, transpose: _t, detune: _d, warpSettings: _s, ...rest } = c;
    return {
      ...rest,
      audioKey: render.key,
      audioIs: "source",
      warping: true,
      warpMode: { value: 0, name: "Beats" },
      warpMarkerCount: 0,
      sourceTempo: render.tempo,
      startMarker: 0,
      endMarker: c.duration,
      looping: (c as { slot?: number }).slot !== undefined && c.looping,
      loopStart: 0,
      loopEnd: c.duration,
    };
  };
  if (!arrangement.audioTracks.some((t) => t.arrangementClips.some((c) => c.render) || (t.sessionClips ?? []).some((c) => c.render))) {
    return arrangement;
  }
  return {
    ...arrangement,
    audioTracks: arrangement.audioTracks.map((t) => ({
      ...t,
      arrangementClips: t.arrangementClips.map(bake),
      ...(t.sessionClips ? { sessionClips: t.sessionClips.map((c) => ({ ...bake(c), slot: c.slot })) } : {}),
    })),
  };
}
